import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, from, mergeMap, of, Subject, tap } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { Checklist } from '../models/checklist.model';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { constants } from '../utils/constants';
import { AppHeader } from '../models/appHeader.model';
import { Storage } from '@ionic/storage-angular';
import { Device, DeviceInfo } from '@capacitor/device';

@Injectable({
  providedIn: 'root'
})
export class CommonService {

  private sourceData = new BehaviorSubject<string>('service');
  private tabPosition = new BehaviorSubject<number>(0);

  private checklistUpdate = new BehaviorSubject<boolean>(false);

  private checklist: any[] = [];

  private keysToRemove = [
    constants.ACCESSIBLE_MODE,
    constants.ITEM_ID,
    constants.CRICINFO,
    constants.ORGANISATION,
    constants.ADDRESS,
    constants.PARTIES,
    constants.RISK,
    constants.DECLARATION,
    constants.ITEM_CREATED_DT
  ];

  private schemeId: string='';
  private AFLSchemeId:string='';
  private schemeCode = environment.schemeCode;
  private tokens: any;
  private apiUrl = environment.api_url;
  private tokenGenKey = environment.tokenGenKey;
  private applicationName = environment.applicationName;
  private applicationVersion = environment.applicationVersion;

  public appHeader: AppHeader = {
    applicationName: '',
    applicationVersion: '',
    deviceId: '',
    deviceOS: '',
    deviceInfo: '',
    checklistId: '',
    disptachedDateTime: '',
    creationDateTime: ''
  };
  // private _storage: Storage | null = null;

  constructor(
    private httpClient: HttpClient,
    private storage: Storage    
  ) { 
    this.init();
    this.getSchemeIdByschemeCode(this.schemeCode);
    this.deviceInfo()
      .then(device => {
        this.appHeader.applicationName = device.applicationName;
        this.appHeader.applicationVersion = device.applicationVersion;
        this.appHeader.deviceId = device.deviceId;
        this.appHeader.deviceInfo = device.deviceInfo;
        this.appHeader.deviceOS = device.deviceOS;
      });
  }

  // Initialize Ionic Angular storage
  async init() {
    await this.storage.create();
}

  public getSourceOfData() {
    return this.sourceData.asObservable();
  }

  public getCurrentTabPosition() {
    return this.tabPosition.asObservable();
  }

  public getChecklistUpdate() {
    return this.checklistUpdate.asObservable();
  }

  public pushSourceOfData(message: any) {
    this.sourceData.next(message);
  }

  public pushCurrentTabPosition(position: number) {
    this.tabPosition.next(position);
  }

  public pushChecklistUpdate(update: boolean) {
    this.checklistUpdate.next(update);
  }

  public clearAllLocalstorage() {
    this.keysToRemove.forEach(key => { 
      this.removeItem(key);
    });
  }

  public getCurrentDate(): string {
    let today = new Date();
    let dd = String(today.getDate()).padStart(2, '0');
    let mm = String(today.getMonth() + 1).padStart(2, '0'); //January is 0!
    let yyyy = today.getFullYear();
    let hh = String(today.getHours()).padStart(2, '0');
    let minute = String(today.getMinutes()).padStart(2, '0');
    let ss = today.getSeconds();
    let currentDate = dd + '-' + mm + '-' + yyyy + ' ' + hh + ':' + minute;
    return currentDate;
  }

  public generateUUID() {
    let itemId: string = uuidv4();
    console.log(itemId);
    return itemId;
  }

  public getAllChecklistData() {
    return JSON.parse(localStorage.getItem(constants.CHECKLIST) || '[]');
  }

  public saveChecklistToLocalStorage() {
    localStorage.setItem(constants.CHECKLIST, JSON.stringify(this.checklist));
  }

  public getCheckItemById(itemId: any): Checklist|any {
    if (itemId != null) {
      this.checklist = [...this.getAllChecklistData()];
      const index = this.checklist.findIndex(item => item.itemId == itemId);
      if (index > -1) {
        return this.checklist[index];
      }
    }
  }

  public getToken() {
    const headers: HttpHeaders = new HttpHeaders({
      'Cache-Control': 'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
      Pragma: 'no-cache',
      Expires: '0'      
    });
    //console.log('API URL --> ' + this.apiUrl);
    return this.httpClient.post(`${this.apiUrl}/getToken`, this.tokenGenKey, { headers, responseType: 'text' })
      .pipe(
        tap(token => {
          localStorage.setItem('tokenJWT', token); // Store the token in local storage
        }),
        catchError(error => {
          //alert('Error occurred while getting token:' + JSON.stringify(error)); // Log the error
          return of(null); // Return a null observable to keep the stream alive
        })
      );
  }

  public postData(postBody: string) {
    this.calculateBodySize(postBody);
    return from(this.getToken())
      .pipe(mergeMap(token => {
        const headers = new HttpHeaders().append('Content-Type', 'application/json').append('Authorization', 'Bearer ' + token);
        var orgInfoBody = {
          "flowName": "get-doc-generic",
          "inputRequest": { "irequest": "{\"statusCode\":\"REQ\",\"requestType\":\"RQ\",\"schemeId\":\"" + this.schemeId + "\",\"schemeCode\":\"" + this.schemeCode + "\",\"requestJSON\":\"" + postBody.toString() + "\"}", "fileList": "{}" },
          "outputKey": "mailResponse"
        };
        //console.log(orgInfoBody);
        return this.httpClient.post(`${this.apiUrl}/invokeWorkflow`, JSON.stringify(orgInfoBody), { headers })
      }
    ));
  }

  public async processChecklistItem(itemId: any): Promise<String | any> {
    let checkItem: Checklist = await this.getItem(itemId);
    let referenceId: string = '';

    if (checkItem.appHeader && checkItem.declaration.submissionDateTime && checkItem.referenceId == '') {
      checkItem.appHeader.dispatchDateTime = this.getCurrentDate();
      let currentData = JSON.stringify(checkItem);
      this.checklist = [...this.getAllChecklistData()];

      let re = /\"/gi;
      let finalRequest = currentData.replace(re, "\\\"");

      this.postData(finalRequest)
        .subscribe({
          next: (success: any) => {
            if (success.referenceNo != '') {
              checkItem.referenceId = success.referenceNo;
              referenceId = success.referenceNo;
            } else {
              if (checkItem.appHeader) {
                checkItem.appHeader.dispatchDateTime = '';
              }
            }

            this.setItem(checkItem.itemId, checkItem);
            this.pushChecklistUpdate(true);
            return referenceId;
          },
          error: (error) => {
            return referenceId;
          }
        });
    }


    this.saveChecklistToLocalStorage();
    this.pushChecklistUpdate(true);
    return referenceId;
  }

  public getSelectedItem(item: string): any[] {
    var lovs = new Array();
    if (localStorage.getItem(item) !== null) {
      return JSON.parse(localStorage[item] || '[]');
    }
    this.getSelectionList(item).subscribe((data: any) => {
      for (var i = 0; i < data.length; i++) {
        lovs.push(data[i].selectionName);
      }
      localStorage.setItem(item, JSON.stringify(lovs));
    });
    return lovs;
  }

  public getSelectionList(type: any) {
    return from(this.getToken())
      .pipe(mergeMap(token => {
        const headers = new HttpHeaders().append('Content-Type', 'application/json').append('Authorization', 'Bearer ' + token);
        return this.httpClient.get(`${this.apiUrl}/getData?destination=getSelListOnSchCodeType?schemeCode=MOBILEAPPS,type=` + type, { headers: headers })
      }
      ));
  }

  public deleteChecklistItem(itemId: any) {
    this.checklist = [...this.getAllChecklistData()];
    const index = this.checklist.findIndex(item => item.itemId === itemId);
    if (index > -1) {
      this.checklist.splice(index, 1);
      this.saveChecklistToLocalStorage();
      alert('Item delete successfully!');
    }
  }

  public setCurrentMode(mode: string, checkItem: Checklist) {
    this.setItem(constants.ACCESSIBLE_MODE, mode);
    this.setItem(constants.ITEM_ID, checkItem.itemId);
    this.setItem(constants.CRICINFO, checkItem);
    this.setItem(constants.ORGANISATION, checkItem.organisation);
    this.setItem(constants.ADDRESS, checkItem.address);
    this.setItem(constants.PARTIES, checkItem.parties);
    this.setItem(constants.RISK, checkItem.risk);
    this.setItem(constants.DECLARATION, checkItem.declaration);
    this.setItem(constants.ITEM_CREATED_DT, checkItem.appHeader.creationDateTime as string);
  }

  public async storeChecklistItem(itemId: string, level: string, data: any, email?: any, finalSubmit?: boolean) {
    localStorage.removeItem(level);
    localStorage.setItem(level, JSON.stringify(data));
    localStorage.setItem(constants.ITEM_ID, itemId);

    if (level === constants.ORGANISATION) {
      localStorage.setItem(constants.ITEM_CREATED_DT, this.getCurrentDate());
    }

    let cricInfo = {
      'appHeader': {
        applicationName: this.appHeader.applicationName,
        applicationVersion: this.appHeader.applicationVersion,
        deviceId: this.appHeader.deviceId,
        deviceOs: this.appHeader.deviceOS,
        deviceInfo: this.appHeader.deviceInfo,
        checklistId: itemId,
        disptachedDateTime: finalSubmit ? this.getCurrentDate() : '',
        creationDateTime: localStorage.getItem('itemCreatedDT') as string,
      },
      'organisation': JSON.parse(localStorage.getItem(constants.ORGANISATION) || '{}'),
      'address': JSON.parse(localStorage.getItem(constants.ADDRESS) || '{}'),
      'parties': JSON.parse(localStorage.getItem(constants.PARTIES) || '{}'),
      'risk': JSON.parse(localStorage.getItem(constants.RISK) || '{}'),
      'declaration': JSON.parse(localStorage.getItem(constants.DECLARATION) || '{}'),
      'itemId': itemId,
      'email': email,
      'schemId': this.schemeId,
      'schemeCode': this.schemeCode,
      'schemeName': this.schemeCode,
      'referenceId': ''
    };

    localStorage.setItem(constants.CRICINFO, JSON.stringify(cricInfo));
    this.checklist = this.getAllChecklistData();

    let indexToUpdate = this.checklist.findIndex(item => item.itemId === cricInfo.itemId);
    if (indexToUpdate > -1) {
      this.checklist[indexToUpdate] = cricInfo; // if we have existing data then update
    } else {
      this.checklist.push(cricInfo);
    }

    localStorage.setItem(constants.CHECKLIST, JSON.stringify(this.checklist));
    if (finalSubmit) {
      var aflData = JSON.stringify(cricInfo);
      let replaceChar = /\"/gi;
      let replaceNewLineChar = /\\n/gi;
      let finalRequest = aflData.replace(replaceChar, "\\\"").replace(replaceNewLineChar,"");      
      this.postData(finalRequest)
        .subscribe({
          next: (suc: any) => {
            if (suc.referenceNo !== '') {
              cricInfo.referenceId = suc.referenceNo;
            } else {
              cricInfo.appHeader.disptachedDateTime = '';
            }
            let indexToUpdate = this.checklist.findIndex(item => item.itemId === cricInfo.itemId);
            if (indexToUpdate > -1) {
              this.checklist[indexToUpdate] = cricInfo; // Here we need to update existing data then update
            } else {
              this.checklist.push(cricInfo);
            }
            localStorage.setItem(constants.CHECKLIST, JSON.stringify(this.checklist));
            this.pushChecklistUpdate(true);
            return cricInfo.referenceId;
          }, error: (error) => {
            localStorage.setItem(constants.CHECKLIST, JSON.stringify(this.checklist));
            return itemId;
          }
        });
      
    }
    localStorage.setItem(constants.CHECKLIST, JSON.stringify(this.checklist));
    this.pushChecklistUpdate(true);
    return itemId;
  }

  getSchemeIdByschemeCode(schemeCode: string) {
    this.getSchemesList().subscribe((data: any) => {
      for (var i = 0; i < data.length; i++) {
        if (data[i].schemeCode === schemeCode) {
         this.schemeId = data[i].schemeId;
          //console.log(this.schemeId);
          //break;
        }
        else if(data[i].schemeCode === 'AFL')
        {
          this.AFLSchemeId = data[i].schemeId;          
        }
      }
    });
  }

  getSchemesList(): any {
    return from(this.getToken())
      .pipe(mergeMap(token => {
        const headers = new HttpHeaders().append('Content-Type', 'application/json').append('Authorization', 'Bearer ' + token);
        return this.httpClient.get(`${this.apiUrl}/getData?destination=getSchemesList`, { headers: headers })
      }));
  }
  
  getFilteredAssocAndClubList(entityType:any, stateCode: any, firstNameVal:any, hostName?:any): any[] {
    //var dataTesting; // When we need to investigate this filter so uncomment and test this.
    var selectionList = new Array();
    this.getFilterData(entityType, stateCode, firstNameVal, hostName).subscribe((data: any) => {
      for (var i = 0; i < data.length; i++) {
         if (data[i].outputJSON) {          
            const InputJsonObject = JSON.parse(data[i].inputJSON);
            if(InputJsonObject.memberCode.includes(entityType)){ //for this consition we only get the Association/Club data.
             const outputJsonObject = JSON.parse(data[i].outputJSON);
             selectionList.push(outputJsonObject.firstName);
             //dataTesting.push(InputJsonObject.memberCode+"---"+outputJsonObject.firstName); // When we need to investigate this filter so uncomment and test this.
            }                     
         }
      }            
    });
    //console.log('Data Test'+JSON.stringify(dataTesting)); // When we need to investigate this filter so uncomment and test this.
    return selectionList;
  }

  getFilterData(entityType:any, stateCode:any, firstName:any, hostName?:any):any {
    return from(this.getToken())
      .pipe(mergeMap(token => {
        const headers = new HttpHeaders().append('Content-Type', 'application/json').append('Authorization', 'Bearer ' + token);
        var requestBody;
        if(entityType.includes('ASSO'))
        {
          requestBody = {
            "methodName": "fetchDataMappingList",
            "inputJSON": "{\"sort\":\"id\",\"order\":\"desc\",\"page\":\"0\",\"filters\":[{\"key\":\"$.firstName\",\"value\":\""+firstName+"\",\"op\":\"CONTAIN\"},{\"key\":\"$.host\",\"value\":\""+firstName+"\",\"op\":\"CONTAIN\"},{\"key\":\"$.codeWebsite\",\"value\":\""+stateCode+"\",\"op\":\"CONTAIN\"},{\"key\":\"status\",\"value\":\"A\",\"op\":\"EQ\"},{\"key\":\"SCHEME_ID\",\"value\":\""+this.AFLSchemeId+"\",\"op\":\"EQ\"}]}"
          }
        }
        else { // This condition only apply for Club
          requestBody = {
          "methodName": "fetchDataMappingList",
          "inputJSON": "{\"sort\":\"id\",\"order\":\"desc\",\"page\":\"0\",\"filters\":[{\"key\":\"$.firstName\",\"value\":\""+firstName+"\",\"op\":\"CONTAIN\"},{\"key\":\"$.host\",\"value\":\""+hostName+"\",\"op\":\"EQ\"},{\"key\":\"$.codeWebsite\",\"value\":\""+stateCode+"\",\"op\":\"CONTAIN\"},{\"key\":\"status\",\"value\":\"A\",\"op\":\"EQ\"},{\"key\":\"SCHEME_ID\",\"value\":\""+this.AFLSchemeId+"\",\"op\":\"EQ\"}]}"
        }
      }
        //console.log('requestBody :'+JSON.stringify(requestBody));
        return this.httpClient.post(`${this.apiUrl}/fetchData?destination=fetchDataMappingList`, JSON.stringify(requestBody), { headers });        
      }
    ));
  }

  public async insertChecklistItem(itemId: string, level: string, data: any, email?: any, finalSubmit?: boolean) {
      this.removeItem(level);
      this.setItem(level, data);
      this.setItem(constants.ITEM_ID, itemId);

    if (level === constants.ORGANISATION) {
      this.setItem(constants.ITEM_CREATED_DT, this.getCurrentDate());
    }

    let cricInfo = {
      'appHeader': {
        applicationName: this.appHeader.applicationName,
        applicationVersion: this.appHeader.applicationVersion,
        deviceId: this.appHeader.deviceId,
        deviceOs: this.appHeader.deviceOS,
        deviceInfo: this.appHeader.deviceInfo,
        checklistId: itemId,
        disptachedDateTime: finalSubmit ? this.getCurrentDate() : '',
        creationDateTime: await this.getItem('itemCreatedDT') as string,
      },
      'organisation': await this.getItem(constants.ORGANISATION) || '{}',
      'address': await this.getItem(constants.ADDRESS) || '{}',
      'parties': await this.getItem(constants.PARTIES) || '{}',
      'risk': await this.getItem(constants.RISK) || '{}',
      'declaration': await this.getItem(constants.DECLARATION) || '{}',
      'itemId': itemId,
      'email': email,
      'schemId': this.schemeId,
      'schemeCode': this.schemeCode,
      'schemeName': this.schemeCode,
      'referenceId': ''
    };
    //console.log('Level:'+level+ '---'+JSON.stringify(cricInfo));  
    this.setItem(constants.CRICINFO, cricInfo);
    this.setItem(cricInfo.itemId, cricInfo);
    
    if (finalSubmit) {
      var aflData = JSON.stringify(cricInfo);
      let replaceChar = /\"/gi;
      let replaceNewLineChar = /\\n/gi;
      let finalRequest = aflData.replace(replaceChar, "\\\"").replace(replaceNewLineChar,"");      
      this.postData(finalRequest)
        .subscribe({
          next: (suc: any) => {
            if (suc.referenceNo !== '') {
              cricInfo.referenceId = suc.referenceNo;
            } else {
              cricInfo.appHeader.disptachedDateTime = '';
            }
            this.setItem(cricInfo.itemId, cricInfo);
            this.pushChecklistUpdate(true);
            return cricInfo.referenceId;
          }, error: (error) => {
            this.setItem(cricInfo.itemId, cricInfo);
            return itemId;
          }
        });      
    }
    this.setItem(cricInfo.itemId,cricInfo);
    this.pushChecklistUpdate(true);
    return itemId;
  }

  // public setStorage(key: string, value: any) {
  //   this.storage?.set(key, value);
  // }

  public setItem(key: string, value: any) {
    this.storage?.set(key, value);
  }

  public async getItem(key: string): Promise<Checklist | any> {
    // this.init();
    return await this.storage?.get(key);    
  }

  public async removeItem(key: string) {
    // this.init();
    await this.storage?.remove(key);
  }

  public clearAllData() {
    this.storage?.clear();
  }

  public async getAllItem(): Promise<Checklist | any> {
    const allItemsList: any[] = [];
    // this.init();
    await this.storage?.forEach((value, key, index) => {
      allItemsList.push(JSON.parse(JSON.stringify(value)));
      // if (!this.keysToRemove.includes(key)) {
      // }
    });
    return [...allItemsList];
  }

  async deviceInfo(): Promise<AppHeader> {
    // Here is full code: https://ionicframework.com/docs/native/device
    const deviceInfo = await Device.getInfo();
    let appHeader: AppHeader = {
      applicationName: this.applicationName,
      applicationVersion: this.applicationVersion,
      deviceId: (await Device.getId()).identifier,
      deviceOS: deviceInfo.operatingSystem,
      deviceInfo: deviceInfo as unknown as string,
      checklistId: '',
      disptachedDateTime: '',
      creationDateTime: '',
    };
    return appHeader;
  }

  public checkIfObjectEmpty(obj: object): boolean {
    return (obj === null 
      || obj === undefined 
      || Object.keys(obj).length === 0);
  }

  public checkIfArrayEmpty(arr: Array<any>): boolean {
    return !Array.isArray(arr) || arr.length < 1;
  }

  public calculateBodySize(data: any) {
    const jsonData = JSON.stringify(data);
    const bodySize = new TextEncoder().encode(jsonData).length;
    console.log(`Body size: ${bodySize} bytes`);
    console.log(`Body jsonData: ${jsonData.length} bytes`);
  }

  public checkIfNullOrUndefined(item: any) {
    if (item == null || item == undefined) {
      return true;
    }
    return false;
  }

}
